import { ConsignmentApiModel, MarketplaceOrderApiModel } from '~/lib/api/smartsend/types';
import { consignmentIsPristine } from './syncOrderLines';
import { OverflowContract } from './types';

/**
 * Floor for how many parcel options a consignment offers when padding is available. A single
 * quantity-1 order can still need several physical boxes, so that choice must not be capped at one.
 */
export const MIN_PARCEL_OPTIONS = 10;

const unitsIn = (rows: { Quantity?: number }[]) => rows.reduce((total, row) => total + (row.Quantity || 1), 0);

/**
 * How many parcels the user may choose. This follows the OVERFLOW CONTRACT the flow will allocate
 * under, not the outcome - which is the correction here. It used to key off PlanOutcome and floor
 * ONE_SHIPMENT at MIN_PARCEL_OPTIONS unconditionally, but ONE_SHIPMENT is only sometimes padded:
 * GenerateConsignmentFromItem pads a single order line and clamps everything else, and
 * RedistributeConsignmentParcels always clamps. So the picker offered up to ten options while
 * distributeIntoParcels silently clamped at the piece count, and every option above that count
 * produced an identical plan - selecting it appeared to do nothing at all.
 *
 * `allocatablePieces` is whatever the caller's flow can actually split: unit count for the flows that
 * break rows apart, row count for the ones that move whole rows.
 */
export function parcelCountBound(overflow: OverflowContract, allocatablePieces: number): number {
  if (overflow === 'pad-with-copies') {
    return Math.max(MIN_PARCEL_OPTIONS, allocatablePieces);
  }

  return Math.max(1, allocatablePieces);
}

/**
 * Whether surplus parcels can be padded out with copies, which needs exactly one order line.
 *
 * With several lines there is no way to know which line a surplus parcel would be for, so the parcel
 * would have to go out empty - and an empty parcel is priced by QuoteHelper.MapQuoteRequest and then
 * dropped by OrderMapper.MapParcels, which is precisely what ITEM-PARCEL-EMPTY exists to prevent.
 * Padding a multi-line order is therefore not merely unimplemented, it is incoherent.
 */
export const canPadAcrossParcels = (lines: MarketplaceOrderApiModel[]): boolean => lines?.length === 1;

/** Units currently declared across a consignment's parcels, which is all redistribution can allocate. */
export const consignmentUnits = (existing?: ConsignmentApiModel): number =>
  unitsIn((existing?.Parcels ?? []).flatMap((parcel) => parcel.Contents ?? []));

/**
 * The parcel-count range for a multi-parcel consignment, matching what the generators will actually
 * produce for this item.
 *
 * Redistribution clamps against the CONSIGNMENT's own units rather than the order's, because rows may
 * have been split, padded or added by hand since. That used to make padding a one-way step: a single
 * quantity-1 line padded to three boxes could never be offered more than three again, because the
 * three padded copies were all redistribution had to work from. The only way back was picking 1x to
 * clear the consignment, which nothing on screen advertised.
 *
 * So a count above the consignment's units is now buildable too, by rebuilding from the order rather
 * than redistributing - see generateConsignment in ConsignmentDetails. That is only lossless while
 * the plan is pristine, so a customised plan still stops at what redistribution can build. Offering
 * more there would mean either discarding the user's edits or showing options that do nothing.
 */
export function consignmentParcelBound(lines: MarketplaceOrderApiModel[], existing?: ConsignmentApiModel): number {
  if (existing?.Parcels?.length > 0 && !consignmentIsPristine(existing, lines)) {
    return parcelCountBound('clamp', consignmentUnits(existing));
  }

  return parcelCountBound(canPadAcrossParcels(lines) ? 'pad-with-copies' : 'clamp', unitsIn(lines ?? []));
}

/**
 * Whether picking `qty` boxes has to rebuild from the order rather than redistribute the rows the
 * plan already has.
 *
 * Redistribution can never produce more parcels than the plan holds units, so a higher count is
 * served by rebuilding - the bundled equivalent of picking 1x and then the new count. Only while the
 * plan is pristine, because rebuilding discards row overrides and Manual rows.
 *
 * Deliberately shares consignmentIsPristine with consignmentParcelBound above, so the range offered
 * and the route taken cannot drift apart. They did once before, and every option past the piece count
 * silently did nothing.
 */
export function shouldRebuildFromOrder(
  existing: ConsignmentApiModel | null,
  lines: MarketplaceOrderApiModel[],
  qty: number
): boolean {
  if (!existing?.Parcels?.length) {
    return true;
  }

  return qty > consignmentUnits(existing) && consignmentIsPristine(existing, lines);
}
